import React, { useContext, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDoubleDown } from '@fortawesome/pro-solid-svg-icons';
import { SurveyDefinition, SurveyStepDefinition } from '../../../../types/Survey';
import { getBackgroundStyles, getNextButtonStyles, getTextStyles } from '../../Util/surveyStyles';
import LocalizedText from '../../../presentational/LocalizedText/LocalizedText';
import SurveyNextButton from '../SurveyNextButton/SurveyNextButton';
import SurveyMarkdown from '../SurveyMarkdown/SurveyMarkdown';
import { SurveyStepContext } from '../../Util/surveyStepContext';

export interface SurveyStepStickyFooterProps {
    surveyDefinition: SurveyDefinition;
    step: SurveyStepDefinition;
    /** When false the Next button is not rendered (e.g. auto-continue QuestionStep). Defaults to true. */
    showNextButton?: boolean;
    nextButtonText?: string;
    isLastStep: boolean;
    onContinue: () => void;
    nextDisabled: boolean;
    /** When true the Skip button is shown. */
    canSkip: boolean;
    onSkip: () => void;
}

export default function SurveyStepStickyFooter({
    surveyDefinition,
    step,
    showNextButton = true,
    nextButtonText,
    isLastStep,
    onContinue,
    nextDisabled,
    canSkip,
    onSkip,
}: SurveyStepStickyFooterProps) {
    const footerRef = useRef<HTMLDivElement>(null);
    const [shadowed, setShadowed] = useState(false);
    const { mode } = useContext(SurveyStepContext) ?? { mode: 'Survey' as const };

    useEffect(() => {
        if (mode === 'Design') return;
        const footer = footerRef.current;
        if (!footer) return;
        let scrollEl: HTMLElement | null = footer.parentElement;
        while (scrollEl && scrollEl !== document.body) {
            const style = window.getComputedStyle(scrollEl);
            if (style.overflowY === 'auto' || style.overflowY === 'scroll' ||
                style.overflow === 'auto' || style.overflow === 'scroll') {
                break;
            }
            scrollEl = scrollEl.parentElement;
        }
        if (!scrollEl || scrollEl === document.body) return;
        const el = scrollEl;
        function updateShadow() {
            setShadowed(el.scrollTop + el.clientHeight < el.scrollHeight);
        }
        updateShadow();
        el.addEventListener('scroll', updateShadow);
        return () => el.removeEventListener('scroll', updateShadow);
    }, []);

    return (
        <div
            ref={footerRef}
            className={`footer${shadowed && mode !== 'Design' ? ' shadowed' : ''}${step.footnote ? ' has-footnote' : ''}`}
        >
            {shadowed && mode !== 'Design' && (
                <div className="scroll-marker-wrapper">
                    <FontAwesomeIcon icon={faAngleDoubleDown} size="2x" className="scroll-marker" />
                </div>
            )}
            {showNextButton && (
                <SurveyNextButton
                    nextButtonText={nextButtonText}
                    isLastStep={isLastStep}
                    style={getNextButtonStyles(surveyDefinition, step, true, true) as React.CSSProperties}
                    onClick={onContinue}
                    disabled={mode !== 'Design' && nextDisabled}
                />
            )}
            {canSkip && (
                <div className="skip-wrapper">
                    <div className="skip-button" onClick={onSkip}>
                        <LocalizedText textKey="skip-question-button-text" />
                    </div>
                </div>
            )}
            {step.footnote && (
                <div className="step-footnote" style={getTextStyles('footnote', surveyDefinition, step, true) as React.CSSProperties}>
                    <SurveyMarkdown text={step.footnote} inline={true} />
                </div>
            )}
        </div>
    );
}
